import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BuildCapError, postBuild } from "./build/api.js";

/**
 * `data-testid` of the single build entry point in the hero. The Pricing
 * CTA scrolls the operator up to this so there is exactly one build form
 * on the surface, never two competing flows.
 */
const HERO_CTA_TESTID = "cta-build-offsider";

export function App() {
  /**
   * Pricing CTA behaviour: smooth-scroll in-page up to the hero build
   * form (the single build entry point), then focus the source input so
   * the operator can start typing immediately. No navigation.
   */
  function scrollToHeroBuild() {
    const cta = document.querySelector<HTMLElement>(
      `[data-testid="${HERO_CTA_TESTID}"]`,
    );
    const form = cta?.closest("form") ?? cta;
    (form ?? cta)?.scrollIntoView({ behavior: "smooth", block: "center" });
    const source = document.querySelector<HTMLInputElement>(
      '[data-testid="build-form-source"]',
    );
    source?.focus({ preventScroll: true });
  }

  return (
    <>
      <Nav />
      <Hero />
      <Verticals />
      <HowItWorks />
      <Handles />
      <WhatYouGet />
      <Pricing onBuildClick={scrollToHeroBuild} />
      <CTA />
      <Footer />
      <OffsiderWidget />
    </>
  );
}

/**
 * Marketing copy, lifted verbatim from `fe-prototype/data.js`. Extracts
 * to a `components/ts/content` brick (or Sanity / a markdown source)
 * once a second surface wants the same copy.
 */
const VERTICAL_KINDS = [
  "Sparkies",
  "Plumbers",
  "Salons",
  "Accountants",
  "Dog day care",
  "Cleaners",
  "Personal trainers",
  "Removalists",
  "Tutors",
  "Cafés",
];

const STEPS = [
  {
    n: 1,
    title: "Share your link",
    body: "Drop in your Google Maps listing or website, or just type your business name and town. We'll find the rest.",
  },
  {
    n: 2,
    title: "Give us a couple of minutes",
    body: "We read what's already out there and build your knowledge base, then generate a voice, greeting and persona, all from public sources.",
  },
  {
    n: 3,
    title: "Talk to your Offsider",
    body: "Test-call it from your phone. Approve or amend anything in the knowledge base, forward your business line, and go live.",
  },
];

const HANDLES = [
  {
    title: "Answers every call",
    body: "In two rings, day or night. Knows your hours, your prices, your patch.",
  },
  {
    title: "Books the job",
    body: "Checks the diary, offers a time, locks it in, then texts you the details.",
  },
  {
    title: "Takes a message",
    body: "When it's not a job it can book, it takes a proper message and flags it.",
  },
  {
    title: "Knows your business",
    body: "Built from your own footprint, services, suburbs you cover, the lot.",
  },
  {
    title: "Covers after hours",
    body: "Picks up the calls you'd otherwise lose at night and on the weekend.",
  },
  {
    title: "Logs everything",
    body: "Every call transcribed and sorted, so nothing slips through the cracks.",
  },
];

const WHAT_YOU_GET = [
  {
    title: "Your Offsider receptionist",
    body: "A voice agent that sounds like you and answers the phone the moment you can't.",
  },
  {
    title: "A living knowledge base",
    body: "Your hours, services, prices and patch, editable any time, in plain language.",
  },
  {
    title: "Calendar bookings",
    body: "Jobs land straight in your diary with the time, the caller and the details.",
  },
  {
    title: "Call & chat summaries by email",
    body: "A tidy recap of every conversation in your inbox, who called and what they needed.",
  },
  {
    title: "A public business page",
    body: "A clean page for your business that callers and customers can find and share.",
  },
  {
    title: "Weekly rediscovery",
    body: "We re-read your site each week and keep your knowledge base current on its own.",
  },
];

const PRICING = {
  price: "$99",
  cadence: "AUD / month",
  activation: "$20 to activate",
  included: "200 minutes a month",
  note: "$20 to activate · $99/mo AUD for 200 minutes · cancel anytime.",
  points: [
    "Answers, books & takes messages",
    "Your number, forwarded",
    "Call transcripts & SMS summaries",
    "Edit the voice & answers any time",
  ],
};

function Nav() {
  return (
    <nav className="nav">
      <div className="wrap nav-inner">
        <a href="#top" className="logo">
          <img src="/img/offsider-monogram.svg" alt="Offsider" />
          <span>Offsider</span>
        </a>
        <div className="nav-links">
          <a href="#how" className="nav-link">
            How it works
          </a>
          <a href="#pricing" className="nav-link">
            Pricing
          </a>
          <a href="#" className="nav-link">
            Sign in
          </a>
          <a href="#build" className="btn btn-primary">
            Get started
          </a>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header id="top" className="section" style={{ paddingTop: "var(--s-8)" }}>
      <div className="wrap grid-2">
        <div>
          <div className="eyebrow">
            <span>For ANZ small business</span>
          </div>
          <h1
            className="t-hero"
            style={{
              marginTop: 16,
              fontSize: "clamp(40px, 6vw, 64px)",
            }}
          >
            Never miss a call.
            <br />
            Even when you're
            <br />
            up a ladder.
          </h1>
          <p className="t-lead" style={{ marginTop: 20, maxWidth: "38ch" }}>
            Offsider is the voice receptionist that sounds like you. Built from
            your own footprint in minutes, it answers the phone, books the job,
            and texts you the details.
          </p>
          <ul
            style={{
              display: "flex",
              alignItems: "center",
              gap: 18,
              marginTop: 28,
              color: "var(--ink-3)",
              fontSize: 13,
              flexWrap: "wrap",
              listStyle: "none",
              padding: 0,
            }}
          >
            <li>✓ ~2 min setup</li>
            <li>✓ No lock-in</li>
            <li>✓ Keep your number</li>
          </ul>
        </div>
        <div id="build">
          <BuildForm />
        </div>
      </div>
    </header>
  );
}

const HERO_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Landing-hero entry form. The single build entry point: captures
 * source + email, POSTs `/builds`, and navigates to `/build/{slug}` so
 * the real scrape pipeline (then the KB admin) takes over. There is no
 * standalone `/build` page any more, so failures surface inline here
 * rather than redirecting.
 */
function BuildForm() {
  const navigate = useNavigate();
  const [source, setSource] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const s = source.trim();
    const em = email.trim();
    if (!s || !HERO_EMAIL_RE.test(em) || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await postBuild({ source: s, email: em });
      navigate(`/build/${encodeURIComponent(res.slug)}`);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof BuildCapError) {
        setSubmitError(
          "You've refreshed a few times today. Try again later, or sign up to refresh more.",
        );
        return;
      }
      setSubmitError("Something went wrong, please try again.");
    }
  }

  const canSubmit =
    source.trim().length > 0 && HERO_EMAIL_RE.test(email.trim()) && !submitting;

  return (
    <div className="try" data-testid="build-form-card">
      <div className="try-head">
        <img src="/img/offsider-monogram.svg" alt="" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700 }}>Build your Offsider</div>
          <div className="t-micro" style={{ color: "var(--ink-3)" }}>
            ~2 minutes · no card needed
          </div>
        </div>
      </div>
      <form
        onSubmit={onSubmit}
        data-testid="build-form"
        style={{ display: "flex", flexDirection: "column", gap: 10 }}
      >
        <div className="field-row">
          <input
            id="source"
            name="source"
            type="text"
            placeholder="Paste a link, or type your business name"
            required
            value={source}
            onChange={(e) => setSource(e.target.value)}
            data-testid="build-form-source"
          />
        </div>
        <div className="field-row">
          <input
            id="email"
            name="email"
            type="email"
            inputMode="email"
            placeholder="Your email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="build-form-email"
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!canSubmit}
            data-testid="cta-build-offsider"
          >
            {submitting ? "Building…" : "Build it"}
          </button>
        </div>
      </form>
      {submitError && (
        <div
          data-testid="build-form-error"
          style={{ fontSize: 13.5, color: "var(--bad)" }}
        >
          {submitError}
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          color: "var(--ink-3)",
          fontSize: 13,
        }}
      >
        <span className="chip chip-line">Google Maps</span>
        <span className="chip chip-line">Your website</span>
        <span className="chip chip-line">Just a name</span>
      </div>
    </div>
  );
}

function Verticals() {
  return (
    <section
      className="section"
      style={{ paddingTop: "var(--s-6)", paddingBottom: "var(--s-6)" }}
    >
      <div className="wrap" style={{ textAlign: "center" }}>
        <div
          className="t-caps"
          style={{ color: "var(--ink-3)", marginBottom: 18 }}
        >
          Built for service businesses that live on the phone
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            justifyContent: "center",
            maxWidth: 720,
            margin: "0 auto",
          }}
        >
          {VERTICAL_KINDS.map((k) => (
            <span key={k} className="chip chip-line">
              {k}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="section" style={{ background: "var(--bg-1)" }}>
      <div className="wrap">
        <div
          style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 56px" }}
        >
          <div className="eyebrow">Live today</div>
          <h2 className="t-display" style={{ marginTop: 14 }}>
            Set up in the time it takes to make a coffee.
          </h2>
          <p style={{ margin: "16px auto 0", color: "var(--ink-2)" }}>
            No scripts to write, no numbers to port by hand. Three steps and
            your phone's covered.
          </p>
        </div>
        <div className="steps">
          {STEPS.map((s) => (
            <div key={s.n} className="card card-hover">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <div className="step-num">{s.n}</div>
              </div>
              <h3 style={{ marginBottom: 8 }}>{s.title}</h3>
              <p style={{ color: "var(--ink-2)", fontSize: 15 }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Handles() {
  return (
    <section id="handles" className="section">
      <div className="wrap">
        <div className="grid-2" style={{ marginBottom: 56, alignItems: "end" }}>
          <div>
            <div className="eyebrow">What it handles</div>
            <h2 className="t-display" style={{ marginTop: 14 }}>
              A capable offsider, not an answering machine.
            </h2>
          </div>
          <p style={{ color: "var(--ink-2)" }}>
            It doesn't just take a name and number. It knows the business, has a
            proper conversation, and gets the job in the diary, then hands you a
            tidy summary.
          </p>
        </div>

        <div className="features-grid">
          {HANDLES.map((f) => (
            <div key={f.title} className="card card-hover">
              <div className="icon-tile" style={{ marginBottom: 16 }}>
                ✓
              </div>
              <h3 style={{ marginBottom: 8 }}>{f.title}</h3>
              <p style={{ color: "var(--ink-2)", fontSize: 15 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatYouGet() {
  return (
    <section id="get" className="section">
      <div className="wrap">
        <div style={{ maxWidth: 620, marginBottom: 48 }}>
          <div className="eyebrow">What you get</div>
          <h2 className="t-display" style={{ marginTop: 14 }}>
            Everything that comes with your Offsider.
          </h2>
          <p style={{ marginTop: 16, color: "var(--ink-2)" }}>
            One setup, the whole kit. It answers, books and remembers, and keeps
            itself up to date without you lifting a finger.
          </p>
        </div>
        <div className="get-grid">
          {WHAT_YOU_GET.map((f) => (
            <div key={f.title} className="get-row">
              <div className="icon-tile" style={{ flexShrink: 0 }}>
                ✓
              </div>
              <div>
                <h3 style={{ marginBottom: 6 }}>{f.title}</h3>
                <p style={{ color: "var(--ink-2)", fontSize: 15 }}>{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface PricingProps {
  onBuildClick: () => void;
}

function Pricing({ onBuildClick }: PricingProps) {
  return (
    <section
      id="pricing"
      className="section"
      style={{ background: "var(--bg-1)" }}
    >
      <div className="wrap" style={{ maxWidth: 760 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div className="eyebrow">Pricing</div>
          <h2 className="t-display" style={{ marginTop: 14 }}>
            One line. One price. No lock-in.
          </h2>
        </div>
        <div
          className="card"
          style={{
            padding: 0,
            overflow: "hidden",
            boxShadow: "var(--shadow-2)",
          }}
        >
          <div
            className="pricing-grid"
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}
          >
            <div style={{ padding: 36, borderRight: "1px solid var(--rule)" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span
                  className="tnum"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                    fontSize: 56,
                    letterSpacing: "-0.04em",
                  }}
                >
                  {PRICING.price}
                </span>
                <span style={{ color: "var(--ink-3)", fontSize: 16 }}>
                  {PRICING.cadence}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  margin: "14px 0 4px",
                }}
              >
                <span className="chip chip-soft">{PRICING.activation}</span>
                <span className="chip chip-line">{PRICING.included}</span>
              </div>
              <p
                style={{
                  color: "var(--ink-2)",
                  fontSize: 15,
                  margin: "12px 0 24px",
                }}
              >
                {PRICING.note}
              </p>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <button
                  type="button"
                  onClick={onBuildClick}
                  className="btn btn-primary btn-lg btn-block"
                  data-testid="cta-build-receptionist"
                >
                  Build my receptionist
                </button>
              </div>
              <div
                className="t-micro"
                style={{
                  textAlign: "center",
                  color: "var(--ink-3)",
                  marginTop: 12,
                }}
              >
                Cancel anytime, no lock-in
              </div>
            </div>
            <div style={{ padding: 36, background: "var(--paper)" }}>
              <div
                className="t-caps"
                style={{ color: "var(--ink-3)", marginBottom: 16 }}
              >
                Everything included
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 18 }}
              >
                {PRICING.points.map((p) => (
                  <div
                    key={p}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      fontSize: 15,
                      lineHeight: 1.4,
                    }}
                  >
                    <span style={{ color: "var(--ok)", flexShrink: 0 }}>✓</span>
                    <span>{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="deep-section">
      <div
        className="wrap section"
        style={{ textAlign: "center", paddingBottom: "var(--s-8)" }}
      >
        <h2 className="t-display" style={{ maxWidth: 640, margin: "0 auto" }}>
          Your phone's ringing right now. Let your Offsider get it.
        </h2>
        <p
          style={{
            maxWidth: 480,
            margin: "16px auto 32px",
            fontSize: 18,
            color: "var(--deep-ink-2)",
          }}
        >
          Build it from a link in two minutes. Test-call it. Go live today.
        </p>
        <a href="#build" className="btn btn-lg btn-onDeep">
          Try a live call
        </a>
      </div>
    </section>
  );
}

function Footer() {
  const cols = [
    { h: "Product", links: ["How it works", "Pricing"] },
    { h: "Company", links: ["About", "Contact"] },
    { h: "Legal", links: ["Privacy", "Terms", "Call recording"] },
  ];
  return (
    <footer
      className="deep-section"
      style={{ borderTop: "1px solid rgba(234,240,234,0.12)" }}
    >
      <div className="wrap" style={{ padding: "56px 24px 40px" }}>
        <div className="footer-cols">
          <div>
            <a href="#top" className="logo">
              <img src="/img/offsider-monogram.svg" alt="Offsider" />
              <span style={{ color: "var(--deep-ink)" }}>Offsider</span>
            </a>
            <p
              style={{
                fontSize: 14,
                marginTop: 16,
                maxWidth: "30ch",
                color: "var(--deep-ink-2)",
              }}
            >
              The voice receptionist for ANZ small business. Answers the phone
              when you can't.
            </p>
            <div
              style={{
                marginTop: 16,
                color: "var(--deep-ink-2)",
                fontSize: 13,
              }}
            >
              Made in Australia · AU & NZ numbers
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.h}>
              <div
                className="t-caps"
                style={{
                  color: "var(--deep-ink)",
                  opacity: 0.7,
                  marginBottom: 10,
                }}
              >
                {c.h}
              </div>
              {c.links.map((l) => (
                <a key={l} href="#" className="footer-link">
                  {l}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 48,
            paddingTop: 24,
            borderTop: "1px solid rgba(234,240,234,0.12)",
            flexWrap: "wrap",
            gap: 12,
            fontSize: 13,
            color: "var(--deep-ink-2)",
          }}
        >
          <span>© 2026 Offsider</span>
          <span>hello@offsider.com.au</span>
        </div>
      </div>
    </footer>
  );
}

/**
 * Bottom-right floating chat launcher — the site dogfoods the product.
 *
 * Replies come from a keyword matcher (`offsiderReply`) so the demo works
 * without any backend or LLM. The real product swaps this for a
 * Bedrock-backed `/chat` endpoint conditioned on the offsider.com.au KB —
 * same Pipecat/Bedrock pattern as the customer-facing receptionist.
 *
 * Voice tab from fe-prototype is deliberately deferred; chat is enough to
 * read the visual intent of the dogfood.
 */
interface ChatMsg {
  who: "them" | "me";
  text: string;
}

function offsiderReply(q: string): string {
  const s = q.toLowerCase();
  if (/price|cost|how much|pricing|pay|expensive|fee/.test(s))
    return "It's $99/month for 200 minutes, plus a one-off $20 to activate. No lock-in, cancel anytime.";
  if (/set ?up|build|start|how do|get going|sign up|onboard/.test(s))
    return "Easy. Share your business link or name, give us a couple of minutes to build from what's public, test it, then forward your line. Most folks are live the same day.";
  if (/do|handle|what can|able|feature/.test(s))
    return "I answer every call, book jobs into your diary, take messages, and text you a summary, day or night.";
  if (/number|keep|forward|port/.test(s))
    return "You keep your number. Just forward your line to your Offsider whenever you can't pick up.";
  if (/voice|sound|accent|persona|tone/.test(s))
    return "You pick the voice and tone, and set how I greet callers. I can sound just like your business. Aussie and NZ voices included.";
  if (/free|trial|try|test|demo/.test(s))
    return "You can chat and talk to your offsider for free while you build it. A real phone test unlocks once you activate ($20).";
  if (/integrat|calendar|stripe|square|xero|calendly|tool/.test(s))
    return "I plug into Google Calendar, Calendly, Stripe, Square, Xero and more. Each connection gives me a new skill, like booking or taking a deposit.";
  if (/cancel|lock|contract|commit/.test(s))
    return "No lock-in at all. Cancel anytime.";
  if (/human|person|real|bot|ai|robot|actually/.test(s))
    return "Ha, you've caught me. You're talking to an Offsider right now. This is the same receptionist we'd build for your business.";
  if (/hour|open|when|available/.test(s))
    return "I'm on 24/7, that's the whole point. I never miss a call, even after hours and weekends.";
  if (/anz|australia|nz|new zealand|local/.test(s))
    return "We're built for ANZ small business, AU and NZ numbers, local voices, the lot.";
  if (/hi|hello|hey|g'day|gday/.test(s))
    return "G'day! Ask me anything about Offsider. Pricing, setup, what I can do. Or hit 'Build it' up top to make your own.";
  if (/thank|cheers|ta\b|great/.test(s))
    return "No worries! Want to build your own? Just hit 'Build it' at the top.";
  return "Happy to help with that. Ask me about pricing, setup, or what I can do. Or hit 'Build it' up top to spin up your own in a couple of minutes.";
}

function OffsiderWidget() {
  const [open, setOpen] = useState(false);
  return open ? (
    <OffsiderPanel onClose={() => setOpen(false)} />
  ) : (
    <button
      className="ow-launch"
      onClick={() => setOpen(true)}
      aria-label="Talk to Offsider"
      data-testid="offsider-widget-launch"
    >
      <img src="/img/offsider-monogram.svg" alt="" />
      Talk to us
      <span className="ow-ping" />
    </button>
  );
}

function OffsiderPanel({ onClose }: { onClose: () => void }) {
  const [msgs, setMsgs] = useState<ChatMsg[]>([
    {
      who: "them",
      text: "G'day! You've reached Offsider, and yep, I'm an Offsider myself, the same receptionist we'd build for you. Ask me anything.",
    },
  ]);
  const [val, setVal] = useState("");
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (bodyRef.current)
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs]);

  function send(text?: string) {
    const q = (text ?? val).trim();
    if (!q) return;
    setMsgs((m) => [...m, { who: "me", text: q }]);
    setVal("");
    // 450ms answer delay — visitor's bubble settles before the reply lands.
    window.setTimeout(() => {
      setMsgs((m) => [...m, { who: "them", text: offsiderReply(q) }]);
    }, 450);
  }

  return (
    <div
      className="ow-panel"
      role="dialog"
      aria-label="Talk to Offsider"
      data-testid="offsider-widget-panel"
    >
      <div className="ow-head">
        <img src="/img/offsider-monogram.svg" alt="" />
        <div style={{ flex: 1 }}>
          <div className="oh-name">Offsider</div>
          <div className="oh-status">
            <i />
            Online · answers in seconds
          </div>
        </div>
        <button className="ow-x" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>

      <div className="ow-meta">
        Yep, our own Offsider is answering this. Same as we'd build for you.
      </div>

      <div className="ow-body" ref={bodyRef}>
        {msgs.map((m, i) => (
          <div key={i} className={`ow-bub ${m.who}`}>
            {m.text}
          </div>
        ))}
      </div>

      <div className="ow-chips">
        {[
          "How much is it?",
          "How does setup work?",
          "Can I keep my number?",
        ].map((c) => (
          <button key={c} className="ow-chip" onClick={() => send(c)}>
            {c}
          </button>
        ))}
      </div>

      <div className="ow-input">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask us anything…"
          aria-label="Message Offsider"
        />
        <button className="ow-send" onClick={() => send()} aria-label="Send">
          ↑
        </button>
      </div>
    </div>
  );
}
